import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DatePicker from 'react-datepicker';
import { STYLES } from '../../consts';
import Heading from '../../../../../../generic-components/Heading';
import { useStore } from '../../../../../../store';
import { ReactComponent as DueDateIcon } from '../../../../../../assets/icons/svg/shared/ic-shared-calendar.svg';
import { convertLocalToUTCStart, getFutureDateFromSpecificDate, getPriorDateFromSpecificDate } from '../../../../../../helpers/datesAndTime.helper';
import { LiliDropdown, LiliDropdownItem } from '../../../../../../DesignSystem/LiliDropdown/LiliDropdown';
import { LiliRadioChipGroup } from '../../../../../../generic-components/LiliRadioChipGroup/LiliRadioChipGroup';
import { BillRecurringEventType } from '../../../../types';
import { getMaxNumOfPaymentsOptions, isOneTimeBill } from '../../../../BillDataUtils';

const FIVE_YEARS_IN_DAYS = 1825 + 1;
const ONE_YEAR_IN_DAYS = 365 + 1;

export const DueDate = () => {
  const { setBillData, billData } = useStore(state => state);
  const [localDateValue, setLocalDateValue] = useState<Date>(billData?.dueDate || new Date());
  const { t } = useTranslation();
  const [paymentFrequency, setPaymentFrequency] = useState<BillRecurringEventType>(BillRecurringEventType.ONE_TIME);
  const [numberOfPaymentsDropDownTitle, setNumberOfPaymentsDropDownTitle] = useState('');
  const [numberOfPaymentsDropDownItem, setNumberOfPaymentsDropDownItem] = useState<LiliDropdownItem | undefined>(undefined);
  const onDatePicked = (date: Date) => {
    const utcStartTime = convertLocalToUTCStart(date);
    setLocalDateValue(utcStartTime);
    setBillData({ ...billData, dueDate: utcStartTime });
  };

  const getDropdownTitle = (frequency: BillRecurringEventType) => {
    switch (frequency) {
      case BillRecurringEventType.ONE_TIME:
        return '';
      case BillRecurringEventType.MONTHLY:
        return t('CREATE_BILL.FORM.PAYMENT_FREQUENCY.UP_TO_MONTHLY');
      case BillRecurringEventType.WEEKLY:
        return t('CREATE_BILL.FORM.PAYMENT_FREQUENCY.UP_TO_WEEKLY');
      default:
        return '';
    }
  };

  const onPaymentFrequencyChanged = (recurringType: BillRecurringEventType) => {
    setPaymentFrequency(recurringType);
    const dropdownTitle = getDropdownTitle(recurringType);
    setNumberOfPaymentsDropDownTitle(dropdownTitle);
    setNumberOfPaymentsDropDownItem(undefined);
    setBillData({
      ...billData,
      recurringPaymentData: { ...billData.recurringPaymentData, paymentFrequency: recurringType, recurringNumber: undefined },
    });
  };

  const numberOfPaymentsOptions = useMemo(() => Array.from({ length: getMaxNumOfPaymentsOptions(paymentFrequency) - 1 }, (_, i) => {
    const value = i + 2;
    return { title: value.toString(), value };
  }), [paymentFrequency]);

  const onNumberOfPaymentsChanged = (selectedItem: LiliDropdownItem) => {
    const dropdownTitle = getDropdownTitle(paymentFrequency);
    setNumberOfPaymentsDropDownTitle(dropdownTitle);
    setNumberOfPaymentsDropDownItem(selectedItem);
    setBillData({
      ...billData,
      recurringPaymentData: { ...billData.recurringPaymentData, recurringNumber: selectedItem.value },
    });
  };

  return (
    <div className={STYLES.CREATE_BILL_BODY.DUE_DATE.WRAPPER}>
      <Heading title={t('CREATE_BILL.FORM.PAYMENT_FREQUENCY.LABEL')} className={STYLES.CREATE_BILL_BODY.PAYMENT_FREQUENCY.TITLE} />
      <div className='flex mb-2'>
        <LiliRadioChipGroup
          items={[
            {
              value: BillRecurringEventType.ONE_TIME,
              label: t('CREATE_BILL.FORM.PAYMENT_FREQUENCY.OPTIONS.ONE_TIME'),
            },
            {
              value: BillRecurringEventType.MONTHLY,
              label: t('CREATE_BILL.FORM.PAYMENT_FREQUENCY.OPTIONS.MONTHLY'),
            },
            {
              value: BillRecurringEventType.WEEKLY,
              label: t('CREATE_BILL.FORM.PAYMENT_FREQUENCY.OPTIONS.WEEKLY'),
            }]}
          onChange={(value: BillRecurringEventType) => onPaymentFrequencyChanged(value)}
          defaultSelectedItemIndex={Object.keys(BillRecurringEventType).indexOf(billData.recurringPaymentData?.paymentFrequency || BillRecurringEventType.ONE_TIME)}
          groupId='reminder-frequency'
        />
      </div>
      {isOneTimeBill(paymentFrequency) ? (
        <>
          <Heading title={t('CREATE_BILL.FORM.DUE_DATE')} className={STYLES.CREATE_BILL_BODY.PAYMENT_FREQUENCY.TITLE} />
          <DatePicker
            id='elDatePicker'
            onChange={onDatePicked}
            selected={localDateValue}
            dropdownMode='select'
            showMonthDropdown
            showYearDropdown
            icon={<DueDateIcon className={STYLES.CREATE_BILL_BODY.DUE_DATE.ICON} />}
            showIcon
            toggleCalendarOnIconClick
            dateFormat='MMM d, yyyy'
            className={STYLES.CREATE_BILL_BODY.DUE_DATE.DATE_PICKER}
            calendarClassName={STYLES.CREATE_BILL_BODY.DUE_DATE.CALENDAR}
            minDate={getPriorDateFromSpecificDate(FIVE_YEARS_IN_DAYS, new Date())}
            maxDate={getFutureDateFromSpecificDate(ONE_YEAR_IN_DAYS, new Date())}
          />
        </>
      ) : (
        <div className='flex space-x-[16px]'>
          <div className='flex flex-col gap-3 w-1/2'>
            <Heading
              title={t('CREATE_BILL.FORM.PAYMENT_FREQUENCY.NUMBER_OF_PAYMENTS')}
              className={STYLES.CREATE_BILL_BODY.PAYMENT_FREQUENCY.TITLE}
            />
            <LiliDropdown
              title={numberOfPaymentsDropDownTitle}
              selectedDropDownItem={numberOfPaymentsDropDownItem}
              onSelect={(item) => onNumberOfPaymentsChanged(item)}
              items={numberOfPaymentsOptions}
              onClear={() => onPaymentFrequencyChanged(paymentFrequency)}
            />
          </div>
          <div className='flex flex-col gap-3 w-1/2'>
            <Heading
              title={t('CREATE_BILL.FORM.PAYMENT_FREQUENCY.FIRST_PAYMENT_DUE')}
              className={STYLES.CREATE_BILL_BODY.PAYMENT_FREQUENCY.TITLE}
            />
            <DatePicker
              id='elDatePicker'
              onChange={onDatePicked}
              selected={localDateValue}
              dropdownMode='select'
              showMonthDropdown
              showYearDropdown
              dateFormat='MMM d, yyyy'
              wrapperClassName='relative w-full'
              icon={<DueDateIcon className={STYLES.CREATE_BILL_BODY.DUE_DATE.ICON} />}
              showIcon
              toggleCalendarOnIconClick
              className={STYLES.CREATE_BILL_BODY.DUE_DATE.DATE_PICKER}
              calendarClassName={STYLES.CREATE_BILL_BODY.DUE_DATE.CALENDAR}
              minDate={getPriorDateFromSpecificDate(FIVE_YEARS_IN_DAYS, new Date())}
              maxDate={getFutureDateFromSpecificDate(ONE_YEAR_IN_DAYS, new Date())}
            />
          </div>
        </div>
      )}
    </div>
  );
};
